import { Buffer } from "buffer";
import { FC, startTransition, useEffect } from "react";
import { useFragment, useRefetchableFragment } from "react-relay";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { graphql } from "relay-runtime";
import {
  AssetLabel,
  Decimal,
  Fiat,
  formatApr,
  i18n,
  IconDenom,
} from "rujira.ui";
import { AngleRight } from "rujira.ui/src/components/icons/Icons";
import { BalanceProvider } from "../../common/components/Balance";
import { usePreloadedAccountData } from "../../services/accountData";
import { useAccounts } from "../../services/accounts";
import { Subscription } from "../../services/useNodeSubscription";
import { GhostVaultAccountFragment$key } from "./__generated__/GhostVaultAccountFragment.graphql";
import { GhostVaultFragment$key } from "./__generated__/GhostVaultFragment.graphql";
import { GhostVaultQuery } from "./__generated__/GhostVaultQuery.graphql";
import { GhostVaultRowFragment$key } from "./__generated__/GhostVaultRowFragment.graphql";
import { GhostVaultSummaryFragment$key } from "./__generated__/GhostVaultSummaryFragment.graphql";
import { AddressLink, Meta, StrategyContainer } from "./Common";
import { GhostVaultBalance } from "./GhostVaultBalance";
import { GhostVaultDeposit } from "./GhostVaultDeposit";

const row = graphql`
  fragment GhostVaultRowFragment on GhostVault {
    id
    asset {
      asset
      type
      chain
      metadata {
        symbol
      }
    }
    deploymentStatus
    status {
      depositPool {
        size
      }
      valueUsd
      apr {
        status
        value
      }
    }
  }
`;

const fragment = graphql`
  fragment GhostVaultFragment on GhostVault
  @refetchable(queryName: "GhostVaultQuery") {
    id
    address
    vaultStatus: status {
      apr {
        status
        value
      }
      depositPool {
        size
      }
      valueUsd
    }

    asset {
      asset
      chain
      type
      metadata {
        symbol
        decimals
      }
      variants {
        native {
          denom
        }
      }
      ...msgAssetFragment
    }
    ...GhostVaultSummaryFragment
  }
`;

const subscription = graphql`
  subscription GhostVaultSubscription($id: ID!) {
    node(id: $id) {
      ... on GhostVault {
        id
        vaultStatus: status {
          apr {
            status
            value
          }
          depositPool {
            size
          }
          valueUsd
        }
      }
    }
  }
`;

const accountFragment = graphql`
  fragment GhostVaultAccountFragment on Account {
    address
    strategies {
      ... on GhostVaultAccount {
        id
        ...GhostVaultBalanceFragment
      }
    }
  }
`;

export const GhostVaultRow: FC<{ k: GhostVaultRowFragment$key }> = ({ k }) => {
  const { id, asset, deploymentStatus, status } = useFragment(row, k);
  const url = `/strategies/lend/${asset.asset}`;

  return (
    <tr className="pointer">
      <Subscription subscription={subscription} id={id} />
      <td>
        <Link
          to={url}
          className="flex ai-c mt-0.5 no-underline color-white w-full">
          <IconDenom denom={asset.metadata.symbol || ""} className="w-4" />
          <h3 className="condensed fs-22 lh-22 fs-lg-28 lh-lg-28 fw-400 mb-0 ml-1 nowrap">
            <AssetLabel
              asset={asset}
              Container={({ children }) => (
                <small className="color-grey fs-14 fs-lg-16">{children}</small>
              )}
            />
          </h3>
        </Link>
      </td>
      {deploymentStatus === "LIVE" ? (
        <td>
          <Link to={url} className="flex dir-c no-underline color-white w-full">
            <Fiat
              amount={BigInt(status.valueUsd)}
              symbol="$"
              decimals={8}
              className="fs-16 fs-md-18 color-white"
            />
            <div className="flex ai-c mt-0.5">
              <Decimal
                amount={BigInt(status.depositPool.size)}
                className="fs-12 fs-md-14 condensed color-grey"
                symbol={asset.metadata.symbol || ""}
              />
            </div>
          </Link>
        </td>
      ) : (
        <td className="text-right">
          <i18n.span className="tag inline-block">Preview only</i18n.span>
        </td>
      )}
      <td className="text-right">{formatApr(status.apr)}</td>
      <td className="text-right">
        <Link to={url} className="block w-full text-right">
          <i18n.div className="tag tag--teal">Lending</i18n.div>
        </Link>
      </td>
      <td className="w-3">
        <Link to={url} className="block w-full text-right">
          <AngleRight className="w-3 h-3 color-grey" />
        </Link>
      </td>
    </tr>
  );
};

export const GhostVault: FC<{
  k: GhostVaultFragment$key;
}> = ({ k }) => {
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<GhostVaultAccountFragment$key>(
    accountFragment,
    accountData
  );
  const { selected } = useAccounts();

  const [data, refetch] = useRefetchableFragment<
    GhostVaultQuery,
    GhostVaultFragment$key
  >(fragment, k);

  useEffect(() => {
    startTransition(() => {
      refetch({}, { fetchPolicy: "store-and-network" });
    });
  }, []);

  return (
    <StrategyContainer>
      <Meta
        title={`Rujira ${data.asset.metadata.symbol} Lending`}
        description={`Provides liquidity to THORChain ${data.asset.metadata.symbol}/RUNE Continuous Liquidity Pools on the Base Layer, using a constant product (XYX) bonding curve. `}
      />
      <div className="flex ai-c wrap">
        <h1 className="h1 mb-0 mr-2">
          <AssetLabel
            asset={data.asset}
            Container={({ children }) => (
              <small className="color-grey fs-20 fs-lg-28">{children}</small>
            )}
          />
        </h1>
        <div className="lp big mr-3">
          <IconDenom denom={data.asset.metadata.symbol} />
        </div>
        <i18n.div className="tag tag--teal">Lending</i18n.div>
      </div>
      <div className="row pad wrap mt-1">
        <div className="col-12 col-md-8 mt-2">
          <div className="row pad wrap">
            <div className="col-12 col-sm-6">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                  Strategy TVL
                </i18n.h3>
                <Fiat
                  symbol="$"
                  amount={BigInt(data.vaultStatus.valueUsd)}
                  decimals={8}
                  className="fs-22 fs-md-32 condensed fw-500 mt-1"
                />
                <div className="flex ai-c mt-1">
                  <IconDenom
                    denom={data.asset.metadata.symbol}
                    className="h-2 w-2 mr-1"
                  />
                  <Decimal
                    amount={BigInt(data.vaultStatus.depositPool.size)}
                    round={4}
                    className="fs-16 fs-md-18 lh-16 lh-md-18 condensed color-grey"
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 mt-2 mt-sm-0 show-sm">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">APR</i18n.h3>
                <p className="fs-22 fs-md-32 condensed fw-500 mt-1">
                  {formatApr(data.vaultStatus.apr)}
                </p>
              </div>
            </div>
            <div className="col-12 mt-4">
              <BalanceProvider asset={data.asset}>
                <GhostVaultDeposit address={data.address} asset={data.asset} />
              </BalanceProvider>
            </div>
          </div>
          <hr className="hr mt-8 mb-8 opacity-4" />

          <Summary k={data} />
        </div>
        <div className="col-12 col-md-4 mt-2 gradient-card-container">
          <GhostVaultBalance
            k={account?.strategies.find(
              (a) =>
                selected &&
                a.id ===
                  Buffer.from(
                    `GhostVaultAccount:${data.address}/${selected.address}`
                  ).toString("base64")
            )}
            symbol={data.asset.metadata.symbol}
          />
        </div>
      </div>
    </StrategyContainer>
  );
};

const summary = graphql`
  fragment GhostVaultSummaryFragment on GhostVault {
    address
    asset {
      metadata {
        symbol
      }
    }
    interest {
      baseRate
      step1
      step2
      targetUtilization
    }
  }
`;

const Summary: FC<{ k: GhostVaultSummaryFragment$key }> = ({ k }) => {
  const { asset, interest, address } = useFragment(summary, k);
  const baseRate = Number(interest.baseRate);
  const step1 = Number(interest.step1);
  const step2 = Number(interest.step2);
  const targetUtilization = Number(interest.targetUtilization);

  const PRECISION = 1e12;
  const SCALE = 1e10;

  const data = Array.from({ length: 101 }, (_, idx) => {
    const util = idx * SCALE;

    const borrow =
      util > targetUtilization
        ? step1 +
          ((util - targetUtilization) / (PRECISION - targetUtilization)) *
            (step2 - step1)
        : baseRate + (util / targetUtilization) * (step1 - baseRate);

    return {
      util: idx / 100,
      lend: (borrow * util) / (PRECISION * PRECISION),
      borrow: borrow / PRECISION,
    };
  });

  return (
    <>
      <i18n.h3 className="h3">Strategy Details</i18n.h3>
      <h4 className="h4">{i18n.t("​Strategy")}: Lending</h4>
      <p className="p">
        Generate passive returns by lending {asset.metadata.symbol}, with a
        relatively low risk profile (all loans are overcollateralized) and no
        exposure to Impermanent Loss. The yield comes from borrowers which are
        charged an interest rate every block based on the utilization rate of
        the borrowed asset compared to the total supplied available for lending.
      </p>

      <i18n.h4 className="h4 mt-5">Interest Rate Curve</i18n.h4>
      <ResponsiveContainer
        width="100%"
        height={285}
        className="my-a mono fs-12">
        <AreaChart
          width={500}
          height={300}
          data={data}
          margin={{
            top: 5,
            right: 5,
            left: 0,
            bottom: 0,
          }}>
          <XAxis
            stroke="#607d8b"
            tick={{ fill: "#ffffff" }}
            fontSize={10}
            dataKey="util"
            tickFormatter={(e) => {
              return (e * 100).toLocaleDecimal(2) + "%";
            }}
            interval={30}
          />
          <YAxis
            stroke="#607d8b"
            tick={{ fill: "#ffffff" }}
            fontSize={10}
            tickFormatter={(e) => {
              return (e * 100).toLocaleDecimal(2) + "%";
            }}
          />
          <Tooltip
            content={
              <CustomTooltip
                active={undefined}
                payload={undefined}
                label={undefined}
              />
            }
          />
          <Area
            type="monotone"
            dataKey="lend"
            stroke="#01CDFE"
            fill="#01CDFE"
            fillOpacity={0.15}
            strokeWidth={2}
          />

          <Area
            type="monotone"
            dataKey="borrow"
            stroke="#01CDFE"
            fill="#01CDFE"
            fillOpacity={0.15}
            strokeWidth={2}
          />

          <ReferenceLine
            x={Number(targetUtilization) / 1e12}
            stroke="#ffffff"
            strokeDasharray="3 4"
          />
        </AreaChart>
      </ResponsiveContainer>
      <i18n.h4 className="h4 mt-5">​Liquidity Withdrawals</i18n.h4>
      <p className="p">
        Lenders may withdraw their liquidity at any time, as long as asset
        utilization remains under 100%. If utilization reaches 100%, lenders'
        funds will be unavailable until debts are repaid or further deposits
        made to the pool.
      </p>
      <i18n.h4 className="h4 mt-5">Contract</i18n.h4>
      <p className="p">
        {i18n.t("Lending Vault")}: <AddressLink address={address} />
      </p>
    </>
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div>
        <p className="label">
          Utilization Rate {(label * 100).toLocaleDecimal(2)}%
          <br />
          Lend Rate:
          {(payload[0].value * 100).toLocaleDecimal(2)}%
          <br />
          Borrow Rate:
          {(payload[1].value * 100).toLocaleDecimal(2)}%
        </p>
      </div>
    );
  }
  return null;
};
